import React, { useState } from 'react';
import { Button } from "@material-tailwind/react";

const CarCardList = ({ cars, onView, onEdit, onDelete }) => {
    const [openMore, setOpenMore] = useState({});

    const toggleMore = (plate) => {
        setOpenMore(prev => ({ ...prev, [plate]: !prev[plate] }));
    };

    if (!cars || cars.length === 0) return null;

    return (
        <div className="space-y-3 block sm:hidden"> {/* mobile-only */}
            {cars.map(car => {
                const safeId = encodeURIComponent(String(car.plate_no || 'car'));
                const expanded = !!openMore[car.plate_no];
                return (
                    <div key={car.plate_no} className="bg-white p-3 rounded-lg shadow-sm border flex flex-col" role="group" aria-labelledby={`card-title-${safeId}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div id={`card-title-${safeId}`} className="text-sm font-semibold text-brand-secondary truncate">{car.plate_no}</div>
                                <div className="text-xs text-gray-500 truncate">{car.driver_name || '—'}</div>
                                <div className={`text-xs truncate ${car.account_manager_name ? 'text-gray-500' : 'text-amber-700'}`}>
                                    Manager: {car.account_manager_name || 'Unassigned'}
                                </div>
                            </div>

                            <div className="text-right flex-shrink-0">
                                <div className="text-sm font-semibold text-green-700">{Number(car.actual_bal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                <div className="text-xs text-gray-500">{(() => {
                                    const dateSource = car.last_war_date ?? car.last_trans_date;
                                    return dateSource ? new Date(dateSource).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';
                                })()}</div>
                            </div>
                        </div>

                        <div className="mt-3 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-gradient-to-r from-[#FF5733] to-[#FF7853] text-white rounded-sm hover:opacity-95 flex-shrink-0 whitespace-nowrap max-w-[96px]"
                                    onClick={() => onView && onView(car.plate_no)}
                                    aria-label={`View ${car.plate_no}`}
                                >
                                    {/* Eye icon (smaller) */}
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                        <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className="text-sm font-medium">View</span>
                                </Button>
                                <button className="hidden sm:inline-flex text-xs px-2 py-1 border rounded-md" onClick={() => onEdit && onEdit(car.plate_no)} aria-label={`Edit ${car.plate_no}`}>Edit</button>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    className="text-xs text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-primary rounded px-2 py-1"
                                    onClick={() => toggleMore(car.plate_no)}
                                    aria-expanded={expanded}
                                    aria-controls={`more-${safeId}`}
                                    aria-label={expanded ? `Collapse details for ${car.plate_no}` : `Expand details for ${car.plate_no}`}
                                >
                                    {expanded ? 'Less' : 'More'}
                                </button>
                            </div>
                        </div>

                        <div
                            id={`more-${safeId}`}
                            role="region"
                            aria-labelledby={`card-title-${safeId}`}
                            className={`${expanded ? 'block' : 'hidden'} mt-3 pt-3 border-t border-gray-100 text-sm text-gray-700`}
                        >
                            <div className="space-y-2">
                                <div className="flex items-start justify-between">
                                    <span className="text-gray-600 font-medium">Insurer</span>
                                    <span className="text-right text-gray-800 truncate ml-4">{car.insurer || 'N/A'}</span>
                                </div>

                                <div className="flex items-start justify-between">
                                    <span className="text-gray-600 font-medium">Insurer Expiry</span>
                                    <span className="text-right text-gray-800 ml-4">{car.insurer_expiry || 'N/A'}</span>
                                </div>

                                <div className="flex items-start justify-between">
                                    <span className="text-gray-600 font-medium">Status</span>
                                    <span className="text-right text-gray-800 ml-4">{car.status || 'N/A'}</span>
                                </div>

                                <div className="flex items-start justify-between">
                                    <span className="text-gray-600 font-medium">Type</span>
                                    <span className={`text-right ml-4 font-medium ${car.usage_type === 'PRIVATE' ? 'text-purple-700' : 'text-sky-700'}`}>
                                        {car.usage_type === 'PRIVATE' ? 'Private' : 'Business'}
                                    </span>
                                </div>

                                <div className="flex items-start justify-between">
                                    <span className="text-gray-600 font-medium">Last Job Card</span>
                                    <span className="text-right text-gray-800 ml-4">
                                        {car.last_job_card_date
                                            ? `${new Date(car.last_job_card_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}${car.last_job_card_no ? ` · ${car.last_job_card_no}` : ''}`
                                            : 'None'}
                                    </span>
                                </div>

                                <div className="flex items-start justify-between">
                                    <span className="text-gray-600 font-medium">Billing</span>
                                    <span className={`text-right ml-4 font-medium ${(car.discount_amount || car.is_discounted) ? 'text-amber-700' : 'text-green-700'}`}>
                                        {(car.discount_amount || car.is_discounted) ? 'Discounted' : 'Billable'}
                                    </span>
                                </div>

                                <div className="flex items-start justify-between">
                                    <span className="text-gray-600 font-medium">Last Pay Date</span>
                                    <span className="text-right text-gray-800 ml-4">{car.last_war_date ? new Date(car.last_war_date).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</span>
                                </div>

                                <div className="flex items-start justify-between">
                                    <span className="text-gray-600 font-medium">Last Pay Amount</span>
                                    <span className="text-right text-gray-800 ml-4 font-mono">{car.last_war_amount ? Number(car.last_war_amount).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}</span>
                                </div>

                                <div className="flex items-start justify-between">
                                    <span className="text-gray-600 font-medium">Last Pay Ref</span>
                                    <span className="text-right text-gray-800 ml-4 truncate">{car.last_war_ref || 'N/A'}</span>
                                </div>

                                <div className="flex items-start justify-between">
                                    <span className="text-gray-600 font-medium">Pay Input By</span>
                                    <span className="text-right text-gray-800 ml-4 truncate">{car.last_war_input_by || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default CarCardList;
